import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Eye, BookOpen, Notebook, MessageCircle, ArrowRight, Sparkles } from 'lucide-react';
import { articlesAPI } from '../services/api';
import ArticleCard from './ArticleCard';

const learningPaths = [
  {
    step: 1,
    icon: Eye,
    title: "Jalur Kesadaran",
    slug: "kesadaran",
    description: "Memahami makna fitrah, peran pendidik, dan fondasi kesadaran diri sebelum mendidik.",
    color: "blue",
  },
  {
    step: 2,
    icon: BookOpen,
    title: "Jalur Belajar",
    slug: "belajar",
    description: "Belajar membangun relasi sadar dengan anak — mendengarkan, bukan mendikte.",
    color: "indigo",
  },
  {
    step: 3,
    icon: Notebook,
    title: "Jalur Refleksi",
    slug: "refleksi",
    description: "Menerapkan ilmu dalam keseharian dan menuliskan jurnal refleksi pribadi.",
    color: "amber",
  },
  {
    step: 4,
    icon: MessageCircle,
    title: "Jalur Dialog",
    slug: "dialog",
    description: "Berdialog dengan sesama pembelajar dan memperdalam pemahaman fitrah.",
    color: "emerald",
  },
];

const colorMap = {
  blue: { bg: 'bg-blue-50', icon: 'bg-blue-100 text-blue-600', border: 'border-blue-200', step: 'bg-blue-600' },
  indigo: { bg: 'bg-indigo-50', icon: 'bg-indigo-100 text-indigo-600', border: 'border-indigo-200', step: 'bg-indigo-600' },
  amber: { bg: 'bg-amber-50', icon: 'bg-amber-100 text-amber-600', border: 'border-amber-200', step: 'bg-amber-600' },
  emerald: { bg: 'bg-emerald-50', icon: 'bg-emerald-100 text-emerald-600', border: 'border-emerald-200', step: 'bg-emerald-600' },
};

const publicSnippets = [
  {
    type: 'refleksi',
    text: '"Saya baru sadar bahwa selama ini saya mendidik anak dengan ketakutan saya sendiri, bukan dengan kesadaran akan fitrahnya."',
    role: 'Orang Tua',
  },
  {
    type: 'dialog',
    text: '"Bagaimana cara membedakan antara membimbing fitrah dan memaksakan harapan kita kepada anak?"',
    role: 'Guru',
  },
  {
    type: 'refleksi',
    text: '"Modul pertama mengubah cara saya melihat peran saya — dari pengajar menjadi penuntun."',
    role: 'Umum',
  },
];

const FeaturesSection = ({ onRegisterClick }) => {
  return (
    <section className="py-20 bg-white">
      {/* Peta Perjalanan Belajar */}
      <div id="perjalanan" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <div className="text-center mb-12">
          <span className="inline-block bg-blue-100 text-blue-700 text-sm font-semibold px-4 py-1 rounded-full mb-4">
            Peta Perjalanan
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Perjalanan Kesadaran <span className="text-blue-600">dengan Urutan</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Ada jalur yang tersusun - dari kesadaran hingga dialog dengan sesama pembelajar.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {learningPaths.map((path) => {
            const IconComponent = path.icon;
            const colors = colorMap[path.color];
            return (
              <Link
                to={`/belajar/${path.slug}`}
                key={path.step}
                className={`relative p-6 rounded-2xl border ${colors.border} ${colors.bg} transition-all duration-300 hover:shadow-lg hover:-translate-y-1 block`}
              >
                {/* Step number */}
                <div className={`absolute -top-3 -left-3 w-8 h-8 ${colors.step} text-white text-sm font-bold rounded-full flex items-center justify-center shadow-md`}>
                  {path.step}
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${colors.icon}`}>
                  <IconComponent className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{path.title}</h3>
                <p className="text-sm text-gray-600">{path.description}</p>
                {path.step < 4 && (
                  <ArrowRight className="absolute top-1/2 -right-4 w-5 h-5 text-gray-300 hidden lg:block" />
                )}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Modul Awal (Gratis) */}
      <div id="mulai-belajar" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-8 md:p-12 text-center text-white">
          <Sparkles className="w-10 h-10 text-white/80 mx-auto mb-4" />
          <h3 className="text-2xl md:text-3xl font-bold mb-3">
            Perjalanan Pertama: Terbuka untuk Semua
          </h3>
          <p className="text-blue-100 max-w-xl mx-auto mb-6">
            Perjalanan dimulai dari kesadaran diri. Tanpa biaya. Tanpa syarat. Cukup hadir dan belajar.
          </p>
          <button onClick={onRegisterClick} className="inline-flex items-center bg-white text-blue-600 hover:bg-blue-50 font-semibold px-8 py-3 rounded-xl text-base shadow-lg transition-colors">
            Daftar & Mulai Perjalanan
            <ArrowRight className="w-5 h-5 ml-2" />
          </button>
        </div>
      </div>

      {/* Cuplikan Dialog & Refleksi Publik */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Suara dari Perjalanan Mereka
          </h3>
          <p className="text-gray-500">
            Cuplikan refleksi dan dialog dari pembelajar Akademia Fitrah
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {publicSnippets.map((snippet, idx) => (
            <div key={idx} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                {snippet.type === 'refleksi' ? (
                  <span className="bg-amber-100 text-amber-700 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                    <Notebook className="w-3 h-3" />
                    Refleksi
                  </span>
                ) : (
                  <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                    <MessageCircle className="w-3 h-3" />
                    Dialog
                  </span>
                )}
                <span className="text-xs text-gray-400">{snippet.role}</span>
              </div>
              <p className="text-gray-700 text-sm italic leading-relaxed">{snippet.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Artikel Terbaru */}
      <ArticlesTerbaruSection />
    </section>
  );
};

const ArticlesTerbaruSection = () => {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    articlesAPI.getAll({ limit: 4 })
      .then((res) => setArticles(res.articles || []))
      .catch(() => {});
  }, []);

  if (articles.length === 0) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
      <div className="text-center mb-10">
        <span className="inline-block bg-indigo-100 text-indigo-700 text-sm font-semibold px-4 py-1 rounded-full mb-4">
          Jelajah Tulisan
        </span>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Artikel Terbaru</h3>
        <p className="text-gray-500">Tulisan, wacana, dan refleksi dari komunitas Akademia Fitrah</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {articles.map((article) => (
          <ArticleCard key={article.id} article={article} />
        ))}
      </div>

      <div className="text-center mt-8">
        <Link
          to="/artikel"
          className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold transition-colors"
        >
          Lihat Semua Artikel
          <ArrowRight className="w-4 h-4 ml-2" />
        </Link>
      </div>
    </div>
  );
};

export default FeaturesSection;
